import PropTypes from 'prop-types'
import React from 'react'
import {stratify, tree} from 'd3-hierarchy'

import './StepsParallel.css'

/**
 * Transform tree coordinates to orient the tree from right to left.
 *
 * See https://bl.ocks.org/mbostock/3184089.
 */
const rightToLeft = (x, y, width, height) => {
  return {
    x: width - y,
    y: x
  };
};

/**
 * Transform tree coordinates to orient the tree from bottom to top.
 *
 * See https://bl.ocks.org/mbostock/3184089.
 */
const bottomToTop = (x, y, width, height) => {
  return {
    x: x,
    y: height - y
  };
};

/**
 * Visualize steps of legislation in Chicago City Council that can happen
 * in parallel.
 */
const StepsParallel = ({steps, width, height}) => {
  const rectWidth = 150;
  const rectHeight = 70;

  const innerWidth = width >= height ? width - (1.5 * rectWidth) : width - (rectWidth / 2);
  const innerHeight = height - rectHeight;
  const margin = {
    left: (width - innerWidth) / 2,
    right: (width - innerWidth) / 2,
    top: (height - innerHeight) / 2,
    bottom: (height - innerHeight) / 2
  };

  // If the viewport is wide rather than tall, show the tree from
  // bottom to top and if it's taller than wide (i.e. mobile), show
  // the tree from right to left.
  //
  // See https://bl.ocks.org/mbostock/3184089.
  //
  // This is a little confusing because our conception of the tree is
  // starting at the leaves and moving toward the "trunk", a final
  // council vote.
  const treeCoordXform = (width >= height ? rightToLeft : bottomToTop);
  const treeSize = (width >= height ? [innerHeight, innerWidth] : [innerWidth, innerHeight]);

  const stepRoot = stratify()
    .id(d => d.slug)
    .parentId(d => d.next_step)(steps);

  const stepRootWithCoords = tree()
    .size(treeSize)(stepRoot);

  const linkAdjust = (width >= height ? rectWidth : rectHeight);

  const lines = stepRootWithCoords.links().map((d, i) => {
    const srcCoords = treeCoordXform(
      d.source.x,
      d.source.y + (linkAdjust / 2),
      width,
      height
    );
    const tgtCoords = treeCoordXform(
      d.target.x,
      d.target.y - (linkAdjust / 2),
      width,
      height
    );
    const pathD = `M${tgtCoords.x},${tgtCoords.y},${srcCoords.x},${srcCoords.y}`;
    return (
      <path key={i} className="link" d={pathD} markerEnd="url(#arrow)" />
    );
  });

  const nodes = stepRootWithCoords.descendants().map((d, i) => {
    const coords = treeCoordXform(d.x, d.y, width, height);
    const icon = d.data.passed === true ? <tspan x={coords.x} dy="1.2em" text-anchor="middle">👍</tspan> : null;
    return (
      <g className="step" key={i}>
        <rect x={coords.x - rectWidth / 2}
              y={coords.y - (rectHeight / 2)}
              width={rectWidth}
              height={rectHeight} />
        <text x={coords.x}
              y={coords.y}
              textAnchor="middle"
              dominantBaseline="middle">
          <tspan x={coords.x} dy="0" text-anchor="middle">{d.data.name}</tspan>
          {icon}
        </text>
      </g>
    );
  });

  const translateX = width >= height ? -1 * margin.left : margin.left;
  const translateY = width >= height ? margin.top : -1 * margin.top;
  const transform = `translate(${translateX}, ${translateY})`;
  return (
    <svg width={width} height={height}>
      <defs>
        <marker id="arrow" viewBox="0 -5 10 10" refX="10" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,-5L10,0L0,5" className="arrow" />
        </marker>
      </defs>
      <g transform={transform}>
        <g className="lines">
          {lines}
        </g>
        <g className="nodes">
          {nodes}
        </g>
      </g>
    </svg>
  );
};

StepsParallel.propTypes = {
  steps: PropTypes.array
}

export default StepsParallel



// WEBPACK FOOTER //
// ./src/StepsParallel.js